import React, { useState, useRef } from 'react'

import { Button, Card, Form, Popconfirm, Table, Icon, Tag, Input } from 'antd'

import {
  getAllSubsctiptionPlanList,
  createSubscriptionPlan,
  deleteSubscriptionPlan,
  renewSubscriptionPlan
} from '../../modules/subscriptionPlan'

import { getIsActivePlanList } from '../../modules/plan'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { useDeepEffect } from '../../utils/hooks'
import Lightbox from 'react-image-lightbox'
import '../../../node_modules/react-image-lightbox/style.css'
import 'moment/locale/th'
import SubscriptionPlanForm from './components/SubscriptionPlanForm'
import { SUBSCRIPTION_PACKAGE_STATUS } from '../../utils/constants'
import Highlighter from 'react-highlight-words'

const GetStatusColor = (status) => {
  let color = ''
  if (status !== null) {
    switch (status.toUpperCase()) {
      case 'ACTIVE':
        color = 'green'
        break
      case 'EXPIRED':
        color = 'red'
        break
      case 'CANCELLED':
        color = 'pink'
        break
      case 'RENEW':
        color = 'orange'
        break
      case 'PENDING':
        color = 'blue'
        break
      default:
        break
    }
  }
  return color
}

const SubscriptionPlan = () => {
  const dispatch = useDispatch()
  const [current, setCurrent] = useState('')
  const [visible, setVisible] = useState(false)
  const [formData, setFormData] = useState({})
  let searchInput = useRef(null)
  const [searchText, setSearchText] = useState('')

  const { subscriptionPlanList, planIsActiveList, isLoading } = useSelector(
    (state) => ({
      subscriptionPlanList: state.subscriptionPlan.subscriptionPlanList,
      planIsActiveList: state.plan.planIsActiveList,
      isLoading: state.subscriptionPlan.isLoading
    }),
    []
  )

  async function fetch() {
    await dispatch(getAllSubsctiptionPlanList())
    await dispatch(getIsActivePlanList())
  }

  useDeepEffect(() => {
    fetch()
  }, [])

  async function viewSlipImage(e, image) {
    e.preventDefault()
    setCurrent(image)
  }

  const handleCloseModal = (e) => {
    e && e.preventDefault()
    setCurrent('')
  }

  const onDelete = async (id) => {
    await dispatch(deleteSubscriptionPlan(id))
    await fetch()
  }

  const onOk = async (data) => {
    if (!isEmpty(formData)) {
      await dispatch(renewSubscriptionPlan(data))
    } else {
      await dispatch(createSubscriptionPlan(data))
    }

    await fetch()
    setVisible(false)
    setFormData({})
  }

  const handleSearch = (selectedKeys, confirm) => {
    confirm()
    setSearchText(selectedKeys[0])
  }

  const handleReset = (clearFilters) => {
    clearFilters()
    setSearchText('')
  }

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={(node) => {
            searchInput = node
          }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm)}
          icon="search"
          size="small"
          style={{ width: 90, marginRight: 8 }}>
          Search
        </Button>
        <Button
          onClick={() => handleReset(clearFilters)}
          size="small"
          style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.select())
      }
    },
    render: (text) => (
      <Highlighter
        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
        searchWords={[searchText]}
        autoEscape
        textToHighlight={text && text.toString()}
      />
    )
  })

  const columns = [
    {
      title: 'วันที่สร้าง',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      sorter: (a, b) => moment(b.createdAt) - moment(a.createdAt), // ✅ เรียงจากล่าสุดไปเก่าสุด
      defaultSortOrder: 'ascend', // ✅ ตั้งค่าเริ่มต้นให้เรียงจากมากไปน้อย
      render: (date) => (
        <span>{moment(date).format('DD/MM/YYYY HH:mm:ss')}</span>
      )
    },
    {
      title: 'LOMAUser',
      dataIndex: 'lomaUserName',
      key: 'lomaUserName',
      width: 110,
      ...getColumnSearchProps('lomaUserName'),
      render: (name, row) => (
        <div>
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={name.toString()}
          />
        </div>
      )
    },
    {
      title: 'Name',
      dataIndex: 'heroName',
      key: 'heroName',
      width: 120,
      ...getColumnSearchProps('heroName'),
      render: (name, row) => (
        <div>
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={name.toString()}
          />
        </div>
      )
    },
    {
      title: 'วันเริ่มต้น',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 120,
      sorter: (a, b) => moment(a.startDate) - moment(b.startDate)
    },
    {
      title: 'วันหมดอายุ',
      dataIndex: 'endDate',
      key: 'endDate',
      width: 120,
      sorter: (a, b) => moment(a.endDate) - moment(b.endDate)
    },
    {
      title: 'ชื่อแพ็กเกจ',
      dataIndex: 'planName',
      key: 'planName',
      width: 120,
      sorter: (a, b) => a.planName.localeCompare(b.planName)
    },
    {
      title: 'ราคา',
      dataIndex: 'planPrice',
      key: 'planPrice',
      width: 120,
      sorter: (a, b) => a.planPrice - b.planPrice, // ✅ เรียงราคาจากน้อยไปมาก
      render: (price) => <span>{price.toLocaleString()} บาท</span> // ✅ แสดงราคาแบบมีจุลภาค
    },
    {
      title: 'วันที่จ่ายเงิน',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 120
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: SUBSCRIPTION_PACKAGE_STATUS.map((status) => ({
        text: status,
        value: status
      })),
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        const color = GetStatusColor(status)
        return (
          <span>
            <Tag color={color} key={status}>
              <a>{status}</a>
            </Tag>
          </span>
        )
      }
    },
    {
      title: 'หลักฐานจ่ายเงิน',
      dataIndex: 'slipImage',
      key: 'slipImage',
      width: 80,
      render: (text, record) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%'
          }}>
          {record.slipImage ? (
            <Icon
              type="eye"
              style={{ color: '#1990FF', fontSize: '24px' }}
              onClick={(e) => viewSlipImage(e, record.slipImage)}>
              {current && (
                <Lightbox mainSrc={current} onCloseRequest={handleCloseModal} />
              )}
            </Icon>
          ) : (
            <Icon type="eye-invisible" style={{ fontSize: '24px' }} />
          )}
        </div>
      )
    },
    {
      title: 'remark',
      dataIndex: 'remark',
      key: 'remark',
      width: 100,
      render: (text) => (text === null ? '-' : text)
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (record) => {
        const canRenew =
          record.status !== 'CANCELLED' &&
          record.status !== 'RENEW' &&
          record.status !== 'PENDING' &&
          record.status !== 'EXPIRED'
        const canCancel =
          record.status !== 'CANCELLED' && record.status !== 'EXPIRED'

        return (
          <span style={{ display: 'flex', gap: '8px' }}>
            {canRenew && (
              <Button
                type="primary"
                size="small"
                onClick={(e) => onClick(e, record)}>
                ต่ออายุ
              </Button>
            )}
            {canCancel && (
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => onDelete(record.id)}>
                <Button type="danger" size="small">
                  ยกเลิก
                </Button>
              </Popconfirm>
            )}
          </span>
        )
      }
    }
  ]

  const onClick = (e, record) => {
    e.preventDefault()
    setVisible(true)
    setFormData(record)
  }

  return (
    console.log('planIsActiveList', planIsActiveList),
    (
      <div>
        <Card
          title="Subscription List"
          extra={
            <div>
              <Button size="small" icon="plus" onClick={(e) => onClick(e)}>
                การสมัครรับ
              </Button>
              <Button size="small" icon="reload" onClick={() => fetch()}>
                Reload
              </Button>
            </div>
          }>
          {!isEmpty(subscriptionPlanList) && (
            <Table
              loading={isLoading}
              scroll={{ x: 1300 }}
              rowKey={(record) => record.id}
              dataSource={subscriptionPlanList}
              columns={columns}
              pagination={{
                pageSizeOptions: ['10', '20', '50', '100'],
                showSizeChanger: true
              }}
            />
          )}
        </Card>
        <SubscriptionPlanForm
          visible={visible}
          onOk={onOk}
          onCancel={() => setVisible(false)}
          formData={formData}
          formPlanData={planIsActiveList}
        />
      </div>
    )
  )
}

const WrappedSubscriptionPlan = Form.create({ name: 'SubscriptionPlan' })(
  SubscriptionPlan
)
export default WrappedSubscriptionPlan
